// Shared state for Idle Session Security (IdleSessionGuard.js).
//
// Kept in localStorage, not React state, for two reasons:
//  - every open tab of the same till reads the same "last activity" time,
//    so working in one tab keeps the others from locking, and locking or
//    unlocking one tab does the same in the rest (via the storage event);
//  - a reload can't be used to skip the lock screen - the locked flag and
//    the idle clock survive it.
// Both keys go away with everything else on LOGOUT (localStorage.clear()),
// and the AUTH reducer drops them on sign-in so a fresh login never inherits
// an old idle clock.

export const LAST_ACTIVITY_KEY = "idleSession.lastActivity";
export const LOCKED_KEY = "idleSession.locked";
// Wrong-password count on the lock screen - stored (not component state) so
// reloading the page doesn't reset the limit.
export const UNLOCK_ATTEMPTS_KEY = "idleSession.unlockAttempts";
// sessionStorage - read once by Login.js to say why the user was signed out.
export const LOGOUT_NOTICE_KEY = "idleSession.logoutNotice";

const safeGet = (key) => {
  try {
    return localStorage.getItem(key);
  } catch (err) {
    return null;
  }
};

const safeSet = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch (err) {
    // Storage blocked - the guard still works within this tab.
  }
};

const safeRemove = (key) => {
  try {
    localStorage.removeItem(key);
  } catch (err) {
    // Nothing to clean up.
  }
};

export const readLastActivity = () => {
  const value = Number(safeGet(LAST_ACTIVITY_KEY));
  return Number.isFinite(value) && value > 0 ? value : null;
};

export const writeLastActivity = (time) => safeSet(LAST_ACTIVITY_KEY, String(time));

export const readLocked = () => safeGet(LOCKED_KEY) === "1";

export const writeLocked = (locked) => (locked ? safeSet(LOCKED_KEY, "1") : safeRemove(LOCKED_KEY));

export const readUnlockAttempts = () => {
  const value = Number(safeGet(UNLOCK_ATTEMPTS_KEY));
  return Number.isInteger(value) && value > 0 ? value : 0;
};

export const writeUnlockAttempts = (count) =>
  count > 0 ? safeSet(UNLOCK_ATTEMPTS_KEY, String(count)) : safeRemove(UNLOCK_ATTEMPTS_KEY);

// SuperAdmin-only, this browser only (never sent to the server, so it can't
// touch any store): when on, the real screen lock fires after 5 seconds of
// no input while the SuperAdmin is viewing the POS - for testing the lock UI.
export const TEST_MODE_KEY = "idleSession.testMode";
export const IDLE_TEST_SECONDS = 5;
export const readTestMode = () => safeGet(TEST_MODE_KEY) === "1";
export const writeTestMode = (on) => (on ? safeSet(TEST_MODE_KEY, "1") : safeRemove(TEST_MODE_KEY));

export const clearIdleState = () => {
  safeRemove(LAST_ACTIVITY_KEY);
  safeRemove(LOCKED_KEY);
  safeRemove(UNLOCK_ATTEMPTS_KEY);
};

// Auto logout is meant to leave nothing of the session behind on a shared
// till: local/session storage, every cookie this page can see (HttpOnly
// ones are out of reach of JS by design), and the Cache Storage API.
export const clearAllClientData = async () => {
  try {
    localStorage.clear();
  } catch (err) {
    // Storage blocked.
  }
  try {
    sessionStorage.clear();
  } catch (err) {
    // Storage blocked.
  }
  try {
    const host = window.location.hostname;
    document.cookie.split(";").forEach((cookie) => {
      const name = cookie.split("=")[0].trim();
      if (!name) return;
      const expired = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
      document.cookie = expired;
      document.cookie = `${expired}; domain=${host}`;
      document.cookie = `${expired}; domain=.${host}`;
    });
  } catch (err) {
    // Cookies disabled.
  }
  try {
    if (window.caches?.keys) {
      const keys = await window.caches.keys();
      await Promise.all(keys.map((key) => window.caches.delete(key)));
    }
  } catch (err) {
    // Cache Storage unavailable (e.g. plain http on a LAN address).
  }
};
